import './BasedataFormView.scss';
import { BuildingType } from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BasedataFormData } from 'src/modules/basedata/types/BasedataFormData';
import { BottomNavigationHelper } from 'src/modules/common/components/BottomNavigationHelper';
import { PageDescription } from 'src/modules/common/components/PageDescription';
import { TileCard } from 'src/modules/common/components/TileCard';
import { BUILDING_TYPE_OPTIONS } from 'src/modules/common/constants/options';
import { useCountryList } from 'src/modules/common/hooks/useCountryList';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { Heading01 } from 'src/modules/common/typography/Heading01';
import { Heading05 } from 'src/modules/common/typography/Heading05';
import { DecimalField } from 'src/modules/form/components/DecimalField';
import { DropdownField } from 'src/modules/form/components/DropdownField';
import { SearchableSelectField } from 'src/modules/form/components/SearchableSelectField';
import { FormError } from 'src/modules/form/types/FormError';
import { ensureNonNull } from 'src/modules/form/utils/transform';

type Props = {
  readonly projectId: string;
  readonly readOnly: boolean;
  readonly isProjectClosed: boolean;
  readonly form: FormController<BasedataFormData, FormError>;
  readonly annualSavingPlaceholderValue: string;
  readonly nextRouteDisabled: boolean;
};

export const BasedataFormView = ({
  projectId,
  annualSavingPlaceholderValue,
  form,
  readOnly,
  isProjectClosed,
  nextRouteDisabled,
}: Props): React.ReactElement => {
  const intl = useIntl();
  const countryCodes = useCountryList();
  return (
    <div className="bp-basedata-form-view">
      <BottomNavigationHelper
        nextRouteDisabled={nextRouteDisabled}
        routeForthText={intl.formatMessage({ id: 'common/bottomNavigation/hvac/settings/next' })}
        routeForthPath={`/project/${encodeURIComponent(projectId)}/hvac/emission`}
      />
      <PageDescription
        className="bp-basedata-form-view__content"
        title="basedata/header"
        description="basedata/description"
      />
      <div className="bp-basedata-form-view__content">
        <div className="bp-basedata-form-view__row">
          <TileCard>
            <div className="bp-basedata-form-view__card-row">
              <DropdownField
                size="md"
                options={BUILDING_TYPE_OPTIONS}
                field={form.field.at('building').at('type').transform(ensureNonNull())}
                label={intl.formatMessage({ id: 'basedata/building/type' })}
                renderOption={renderBuildingTypeOption}
                readOnly={readOnly || isProjectClosed}
              />
              <SearchableSelectField
                size="md"
                label={intl.formatMessage({ id: 'basedata/building/address/country' })}
                field={form.field.at('building').at('address').at('country').transform(ensureNonNull())}
                options={countryCodes}
                formatOptionLabel={(option) => intl.formatMessage({ id: `common/country/name/${option.value}` })}
                menuPlacement="bottom"
                readOnly={readOnly || isProjectClosed}
              />
            </div>
            <div className="bp-basedata-form-view__card-row">
              <DecimalField
                label={intl.formatMessage({ id: 'basedata/annualSaving' })}
                placeholder={intl.formatMessage(
                  { id: 'basedata/annualSaving/placeholder' },
                  { value: annualSavingPlaceholderValue },
                )}
                field={form.field.at('annualSaving')}
                size="md"
                autoComplete="off"
                actions="kWh/m²"
              />
              <div className="full-width">&nbsp;</div>
            </div>
          </TileCard>
        </div>

      </div>
    </div>
  );
};

function renderBuildingTypeOption(option: BuildingType): React.ReactElement {
  return <FormattedMessage id={`common/buildingType/${option}`}/>;
}
